import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  Today as TodayIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { format } from 'date-fns';
import { fetchPlatformStats, PlatformStats } from './platformStatsShared';

interface Business {
  id: number;
  business_id?: string;
  name?: string;
  business_name?: string;
  location?: string;
  address?: string;
  status?: string;
  subscription_status?: string;
  lastCheck?: string;
  last_check_date?: string;
}

const OwnerDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState<number | null>(null);
  const [selectedBusinessName, setSelectedBusinessName] = useState<string>('');
  const [businessSearch, setBusinessSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/');
          return;
        }

        const headers = { Authorization: `Bearer ${token}` };

        const [bizRes, statsRes] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/businesses`, { headers }),
          fetchPlatformStats(),
        ]);

        if (bizRes.status === 'fulfilled') {
          const transformedBusinesses = bizRes.value.data.map((business: any) => ({
            id: business.id || business.business_id,
            name: business.name || business.business_name,
            location: business.location || business.address,
            status: business.status || business.subscription_status || 'Active',
            lastCheck: business.lastCheck || business.last_check_date || 'Not checked',
          }));
          setBusinesses(transformedBusinesses);
        }

        if (statsRes.status === 'fulfilled') {
          setPlatformStats(statsRes.value);
        }
      } catch (err) {
        setError('Failed to fetch dashboard data. Please try again later.');
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleAddBusiness = () => navigate('/owner/add-business');

  const handleViewDetails = (businessId: number) => {
    const business = businesses.find(b => b.id === businessId);
    if (business) {
      setSelectedBusinessId(businessId);
      setSelectedBusinessName(business.name || '');
      setConfirmDialogOpen(true);
    }
  };

  const handleConfirmLogin = async () => {
    try {
      if (!selectedBusinessId) return;
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }
      localStorage.setItem('superAdminToken', token);
      try {
        const response = await axios.post(
          `${API_BASE_URL}/auth/impersonate`,
          { businessId: selectedBusinessId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('impersonatingBusiness', 'true');
          localStorage.setItem('businessName', selectedBusinessName);
          localStorage.setItem('businessId', selectedBusinessId.toString());
          if (response.data.user) localStorage.setItem('user', JSON.stringify(response.data.user));
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          setConfirmDialogOpen(false);
          window.location.href = '/admin/dashboard';
          return;
        }
      } catch (err: any) {
        if (err.response && err.response.status !== 403 && err.response.status !== 404) throw err;
      }
      localStorage.setItem('impersonatingBusiness', 'true');
      localStorage.setItem('businessContext', JSON.stringify({ id: selectedBusinessId, name: selectedBusinessName, isImpersonated: true }));
      localStorage.setItem('businessName', selectedBusinessName);
      localStorage.setItem('businessId', selectedBusinessId.toString());
      setConfirmDialogOpen(false);
      window.location.href = '/admin/dashboard';
    } catch (error) {
      console.error('Error logging into business:', error);
      setError('Failed to log into business. Please try again.');
      setConfirmDialogOpen(false);
    }
  };

  const complianceRate = businesses.length > 0
    ? Math.round((businesses.filter(b => b.status === 'Active' || b.status === 'active').length / businesses.length) * 100)
    : 0;

  const activeCount = businesses.filter(b => b.status === 'Active' || b.status === 'active').length;

  const filteredBusinesses = useMemo(() => {
    const query = businessSearch.trim().toLowerCase();
    const list = !query
      ? businesses
      : businesses.filter(
          b =>
            (b.name || '').toLowerCase().includes(query) ||
            (b.location || '').toLowerCase().includes(query) ||
            (b.status || '').toLowerCase().includes(query)
        );
    return [...list].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  }, [businesses, businessSearch]);

  const enrichedBusinesses = useMemo(() => {
    if (!platformStats) return filteredBusinesses.map(b => ({ ...b, totalChecks: 0, lastActive: null as string | null }));
    const statMap = new Map(
      platformStats.businesses.map(s => [s.name?.toLowerCase(), s])
    );
    return filteredBusinesses.map(b => {
      const stat = statMap.get((b.name || '').toLowerCase());
      return {
        ...b,
        totalChecks: stat?.completedChecks ?? 0,
        lastActive: stat?.lastActive ?? null,
      };
    });
  }, [filteredBusinesses, platformStats]);

  const getMetricCardSx = (color: string) => ({
    p: 3,
    height: '100%',
    borderRadius: 4,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    bgcolor: alpha(color, 0.08),
    border: '1px solid',
    borderColor: alpha(color, 0.25),
    boxShadow: `0 4px 20px ${alpha(color, 0.12)}`,
    '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 32px ${alpha(color, 0.18)}` },
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const metricCards = [
    { key: 'businesses', label: 'Businesses', value: businesses.length.toLocaleString(), helper: 'Total businesses on the platform', color: theme.palette.primary.main, icon: <BusinessIcon fontSize="large" /> },
    { key: 'active', label: 'Active', value: activeCount.toLocaleString(), helper: 'Businesses with active status', color: theme.palette.success.main, icon: <CheckCircleIcon fontSize="large" /> },
    { key: 'today', label: 'Checks Today', value: (platformStats?.checksToday ?? 0).toLocaleString(), helper: 'Checks completed across all businesses today', color: theme.palette.info.main, icon: <TodayIcon fontSize="large" /> },
    { key: 'compliance', label: 'Compliance', value: `${complianceRate}%`, helper: 'Share of businesses with active status', color: theme.palette.warning.main, icon: <AssessmentIcon fontSize="large" /> },
  ];

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, [theme.breakpoints.down('sm')]: { mt: 2, mb: 2, px: 2 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Business Owner Dashboard
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage all your business properties and safety compliance in one place.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<BarChartIcon />}
            onClick={() => navigate('/owner/reports')}
            sx={{ borderRadius: 2, mt: 0.5 }}
          >
            Business Reports
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {metricCards.map(card => (
            <Grid item xs={12} sm={6} md={3} key={card.key}>
              <Card sx={getMetricCardSx(card.color)}>
                <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '16px', backgroundColor: alpha(card.color, 0.2), color: card.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {card.icon}
                    </Box>
                    <Box>
                      <Typography variant="overline" color="text.secondary">{card.label}</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>{card.value}</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{card.helper}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Business Management</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Button variant="contained" color="primary" onClick={handleAddBusiness} sx={{ borderRadius: 2 }}>
                  Add New Business
                </Button>
                <Button variant="outlined" color="primary" startIcon={<BarChartIcon />} onClick={() => navigate('/owner/reports')} sx={{ borderRadius: 2 }}>
                  View Business Reports
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ mb: 4, p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6">All Businesses</Typography>
            <TextField
              size="small"
              placeholder="Search businesses..."
              value={businessSearch}
              onChange={e => setBusinessSearch(e.target.value)}
              sx={{ minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
            <Table>
              <TableHead sx={{ bgcolor: 'background.default' }}>
                <TableRow>
                  <TableCell>Business Name</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Checks Completed</TableCell>
                  <TableCell>Last Active</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enrichedBusinesses.length > 0 ? (
                  enrichedBusinesses.map(business => (
                    <TableRow key={business.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{business.name}</TableCell>
                      <TableCell>{business.location || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={business.status || 'Unknown'}
                          size="small"
                          color={(business.status || '').toLowerCase() === 'active' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {(business.totalChecks ?? 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {business.lastActive
                            ? (() => { try { return format(new Date(business.lastActive), 'dd MMM yyyy'); } catch { return '—'; } })()
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" color="primary" onClick={() => handleViewDetails(business.id)} sx={{ borderRadius: 2 }}>
                          Log In
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {businessSearch.trim()
                        ? `No businesses match "${businessSearch}". Try a different search.`
                        : 'No businesses found. Click "Add New Business" to get started.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Log in as Business</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to log in as "{selectedBusinessName}". This will give you access to all their data and features.
            Your super admin session will be preserved for when you log out.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="primary">Cancel</Button>
          <Button onClick={handleConfirmLogin} color="primary" variant="contained">Log In as Business</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OwnerDashboard;
